import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaCalendarAlt,
  FaUsers,
  FaBed,
  FaReceipt,
  FaCheckCircle,
  FaCreditCard,
  FaUtensils,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaPrint,
} from "react-icons/fa";
import { useSearchParams } from "react-router-dom";

const BookingConfirmation = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("details");

  useEffect(() => {
    const fetchBookingDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://apigulmohar.dodunsoftsolutions.com/api/bookings/${id}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch booking details");
        }

        const data = await response.json();
        setBooking(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    // Fetch real data
    fetchBookingDetails();
  }, [id]);

  // Format dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  // Format transaction date
  const formatTransactionDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Print the confirmation
  const handlePrint = () => {
    window.print();
  };

  // Calculate nights
  const calculateNights = (checkIn, checkOut) => {
    return Math.round(
      (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Define animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.15,
        duration: 0.6,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  // Loading state with elegant spinner
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-amber-50 to-yellow-100">
        <div className="text-center p-8 bg-white rounded-xl shadow-2xl">
          <div className="w-20 h-20 border-4 border-amber-800 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-6 text-amber-800 font-serif text-xl">
            Retrieving your luxury experience...
          </p>
          <p className="mt-2 text-amber-600 italic">The Gulmohar Grand </p>
        </div>
      </div>
    );
  }

  // Error state with premium styling
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-amber-50 to-yellow-100">
        <div className="text-center p-12 bg-white rounded-xl shadow-2xl max-w-lg">
          <div className="text-red-600 text-6xl mb-6">⚠️</div>
          <h2 className="text-3xl font-serif mb-4">Booking Retrieval Failed</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <p className="text-gray-500 mb-6 italic">
            We apologize for the inconvenience. Our concierge team is available
            to assist you.
          </p>
          <div className="flex justify-center space-x-4">
            <button
              className="px-8 py-3 bg-amber-800 text-white rounded-lg hover:bg-amber-900 transition shadow-lg flex items-center"
              onClick={() => window.location.reload()}
            >
              <FaCheckCircle className="mr-2" /> Try Again
            </button>
            <button
              className="px-8 py-3 bg-white border border-amber-800 text-amber-800 rounded-lg hover:bg-amber-50 transition shadow-lg flex items-center"
              onClick={() => (window.location.href = "tel:+919876543210")}
            >
              <FaPhone className="mr-2" /> Contact Us
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  const checkInDate = formatDate(booking.checkIn);
  const checkOutDate = formatDate(booking.checkOut);
  const nights = calculateNights(booking.checkIn, booking.checkOut);

  return (
    <div className="min-h-screen bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-100 py-12 px-4 sm:px-6 lg:px-8 font-sans print:bg-white print:py-0">
      <motion.div
        className="max-w-6xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Logo & Header */}
        <motion.div
          variants={itemVariants}
          className="text-center mb-10 relative"
        >
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 h-px w-1/4 bg-amber-300 hidden md:block"></div>
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 h-px w-1/4 bg-amber-300 hidden md:block"></div>
          <div className="inline-block">
            <div className="w-24 h-24 rounded-full bg-amber-800 mx-auto flex items-center justify-center mb-4">
              <h1 className="text-4xl font-serif font-bold text-white">GG</h1>
            </div>
            <h1 className="text-5xl font-serif font-bold text-amber-800 mb-2">
              The Gulmohar Grand
            </h1>
            <p className="text-lg text-amber-700 italic">
              Where luxury meets tranquility
            </p>
          </div>
        </motion.div>

        {/* Confirmation Banner */}
        <motion.div
          variants={itemVariants}
          className="bg-gradient-to-r from-amber-700 to-amber-900 text-white p-8 rounded-t-xl shadow-xl text-center relative overflow-hidden"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-300 to-amber-300"></div>
          <div className="absolute top-0 left-0 w-24 h-24 rounded-full bg-amber-600 opacity-20 -translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute bottom-0 right-0 w-32 h-32 rounded-full bg-amber-600 opacity-20 translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute top-0 right-0 w-full h-full opacity-5 bg-right-top"></div>

          <div className="flex items-center justify-center space-x-3 mb-3">
            <FaCheckCircle className="text-3xl text-amber-200" />
            <h2 className="text-3xl font-serif font-bold">Booking Confirmed</h2>
          </div>
          <p className="text-amber-200 text-lg mb-4">
            Your exclusive stay awaits you
          </p>
          <div className="inline-block bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
            <p className="text-white">
              Confirmation{" "}
              <span className="font-mono font-bold">
                #{booking.invoiceNumber}
              </span>
            </p>
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-b-xl shadow-xl p-8 mb-8 relative"
        >
          {/* Quick Navigation */}
          <div className="flex overflow-x-auto mb-8 border-b pb-4 hide-scrollbar print:hidden">
            <button
              onClick={() => setActiveSection("details")}
              className={`px-5 py-2 rounded-lg mr-4 whitespace-nowrap ${
                activeSection === "details"
                  ? "bg-amber-800 text-white shadow-md"
                  : "text-amber-800 bg-amber-50 hover:bg-amber-100"
              }`}
            >
              Stay Details
            </button>
            <button
              onClick={() => setActiveSection("room")}
              className={`px-5 py-2 rounded-lg mr-4 whitespace-nowrap ${
                activeSection === "room"
                  ? "bg-amber-800 text-white shadow-md"
                  : "text-amber-800 bg-amber-50 hover:bg-amber-100"
              }`}
            >
              Room Details
            </button>
            <button
              onClick={() => setActiveSection("payment")}
              className={`px-5 py-2 rounded-lg mr-4 whitespace-nowrap ${
                activeSection === "payment"
                  ? "bg-amber-800 text-white shadow-md"
                  : "text-amber-800 bg-amber-50 hover:bg-amber-100"
              }`}
            >
              Payment Information
            </button>
            <button
              onClick={() => setActiveSection("policies")}
              className={`px-5 py-2 rounded-lg mr-4 whitespace-nowrap ${
                activeSection === "policies"
                  ? "bg-amber-800 text-white shadow-md"
                  : "text-amber-800 bg-amber-50 hover:bg-amber-100"
              }`}
            >
              Policies
            </button>
          </div>

          {/* Guest Information - Always visible */}
          <div className="mb-12 print:mb-8">
            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center">
              <span className="w-8 h-8 rounded-full bg-amber-800 text-white flex items-center justify-center mr-3 text-sm">
                <FaUsers />
              </span>
              Guest Information
            </h3>
            <div className="grid md:grid-cols-2 gap-6 bg-amber-50/50 p-6 rounded-xl border border-amber-100">
              <div>
                <p className="text-gray-500 text-sm mb-1">Guest Name</p>
                <p className="text-xl font-medium">{booking.guest.name}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Email Address</p>
                <p className="text-lg">{booking.guest.email}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Contact Number</p>
                <p className="text-lg">{booking.guest.phoneNumber}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Party Size</p>
                <p className="text-lg flex items-center">
                  <span className="inline-flex items-center justify-center w-6 h-6 bg-amber-800 text-white rounded-full mr-2 text-xs">
                    {booking.numberOfGuests}
                  </span>
                  {booking.numberOfGuests === 1 ? "Person" : "People"}
                </p>
              </div>
            </div>
          </div>

          {/* Stay Details */}
          <div
            className={`${
              activeSection !== "details" && "hidden print:block"
            } mb-12`}
          >
            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center">
              <span className="w-8 h-8 rounded-full bg-amber-800 text-white flex items-center justify-center mr-3 text-sm">
                <FaCalendarAlt />
              </span>
              Stay Details
            </h3>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl shadow-sm border border-amber-200">
                <div className="flex items-start">
                  <div className="bg-amber-800 text-white p-3 rounded-lg mr-4 shadow-md">
                    <FaCalendarAlt className="text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm mb-1">Check-in Date</p>
                    <p className="font-serif font-medium text-xl mb-1">
                      {checkInDate}
                    </p>
                    <div className="flex items-center text-sm text-amber-800">
                      <span className="font-medium">After 12:00 PM</span>
                      <span className="h-1 w-1 bg-amber-800 rounded-full mx-2"></span>
                      <span className="text-amber-700">
                        Early check-in available upon request
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl shadow-sm border border-amber-200">
                <div className="flex items-start">
                  <div className="bg-amber-800 text-white p-3 rounded-lg mr-4 shadow-md">
                    <FaCalendarAlt className="text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm mb-1">Check-out Date</p>
                    <p className="font-serif font-medium text-xl mb-1">
                      {checkOutDate}
                    </p>
                    <div className="flex items-center text-sm text-amber-800">
                      <span className="font-medium">Before 12:00 PM</span>
                      <span className="h-1 w-1 bg-amber-800 rounded-full mx-2"></span>
                      <span className="text-amber-700">
                        Late check-out available upon request
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl shadow-sm border border-amber-200">
                <div className="flex items-start">
                  <div className="bg-amber-800 text-white p-3 rounded-lg mr-4 shadow-md">
                    <FaBed className="text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm mb-1">
                      Duration of Stay
                    </p>
                    <p className="font-serif font-medium text-xl">
                      {nights} {nights === 1 ? "Night" : "Nights"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl shadow-sm border border-amber-200">
                <div className="flex items-start">
                  <div className="bg-amber-800 text-white p-3 rounded-lg mr-4 shadow-md">
                    <FaCheckCircle className="text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm mb-1">
                      Reservation Status
                    </p>
                    <div className="flex items-center">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          booking.status === "confirmed"
                            ? "bg-green-100 text-green-800"
                            : booking.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {booking.status.charAt(0).toUpperCase() +
                          booking.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Special Requests */}
            {booking.specialRequests && (
              <div className="mt-8">
                <h4 className="text-lg font-medium text-gray-800 mb-3">
                  Special Requests
                </h4>
                <div className="bg-amber-50 p-6 rounded-xl border border-amber-200">
                  <div className="flex">
                    <FaCalendarAlt className="text-amber-800 text-lg mr-3 mt-1 flex-shrink-0" />
                    <p className="italic text-gray-700">
                      {booking.specialRequests}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Room Details */}
          <div
            className={`${
              activeSection !== "room" && "hidden print:block"
            } mb-12`}
          >
            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center">
              <span className="w-8 h-8 rounded-full bg-amber-800 text-white flex items-center justify-center mr-3 text-sm">
                <FaBed />
              </span>
              Room Details
            </h3>

            {booking.rooms.map((room, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-white p-6 rounded-xl mb-6 shadow-md border border-amber-100"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="mb-4 md:mb-0">
                    <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs rounded-full mb-2">
                      Room {index + 1}
                    </span>
                    <h4 className="font-serif font-bold text-xl text-amber-900 mb-1">
                      {room.roomName}
                    </h4>
                    <p className="text-gray-600 text-sm flex items-center">
                      <FaMapMarkerAlt className="mr-1 text-amber-700" />
                      Room ID:{" "}
                      {typeof room.roomId === "object"
                        ? room.roomId.roomNumber || room.roomId._id
                        : room.roomId}
                    </p>
                  </div>

                  <div className="text-right bg-amber-50 px-6 py-3 rounded-lg">
                    <p className="text-amber-800 font-bold text-2xl font-serif">
                      {formatCurrency(room.price)}
                    </p>
                    <p className="text-gray-500 text-xs">per night</p>
                  </div>
                </div>

                {/* Room amenities - can be expanded as needed */}
                <div className="mt-6 pt-4 border-t border-amber-100">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">
                    Room Amenities
                  </h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                      King Size Bed
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                      Air Conditioning
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                      Mini Bar
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                      Room Service
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Addons if any */}
            {booking.addons && booking.addons.length > 0 && (
              <div className="mt-8">
                <h4 className="text-lg font-medium text-gray-800 mb-4">
                  Additional Services
                </h4>
                <div className="bg-amber-50 rounded-xl overflow-hidden border border-amber-100">
                  {booking.addons.map((addon, index) => (
                    <div
                      key={index}
                      className={`flex justify-between items-center p-4 ${
                        index !== booking.addons.length - 1
                          ? "border-b border-amber-100"
                          : ""
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mr-4">
                          <FaUtensils className="text-amber-700" />
                        </div>
                        <div>
                          <p className="font-medium">{addon.name}</p>
                          <p className="text-xs text-gray-500">
                            Enhanced experience
                          </p>
                        </div>
                      </div>
                      <div className="font-medium text-lg">
                        {formatCurrency(addon.price)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Payment Details */}
          <div
            className={`${activeSection !== "payment" && "hidden print:block"}`}
          >
            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center">
              <span className="w-8 h-8 rounded-full bg-amber-800 text-white flex items-center justify-center mr-3 text-sm">
                <FaReceipt />
              </span>
              Payment Information
            </h3>

            <div className="bg-white rounded-xl shadow-md border border-amber-100 overflow-hidden">
              <div className="bg-amber-800 text-white px-6 py-4 flex items-center justify-between">
                <div className="flex items-center">
                  <FaLock className="mr-2" />
                  <span className="font-medium">Payment Status</span>
                </div>
                <span
                  className={`px-4 py-1 rounded-full text-sm font-medium ${
                    booking.paymentStatus === "paid"
                      ? "bg-green-100 text-green-800"
                      : booking.paymentStatus === "partial"
                      ? "bg-yellow-100 text-yellow-800"
                      : booking.paymentStatus === "refunded"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {booking.paymentStatus.charAt(0).toUpperCase() +
                    booking.paymentStatus.slice(1)}
                </span>
              </div>

              <div className="p-6">
                {/* Transaction History */}
                {booking.amountTransactions &&
                  booking.amountTransactions.length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-lg font-medium text-gray-800 mb-4">
                        Transaction History
                      </h4>
                      <div className="bg-gray-50 rounded-lg overflow-hidden">
                        {booking.amountTransactions.map(
                          (transaction, index) => (
                            <div
                              key={index}
                              className={`flex justify-between items-center p-4 ${
                                index !== booking.amountTransactions.length - 1
                                  ? "border-b border-gray-100"
                                  : ""
                              }`}
                            >
                              <div>
                                <div className="flex items-center">
                                  <span
                                    className={`inline-block w-3 h-3 rounded-full mr-2 ${
                                      transaction.type === "credit"
                                        ? "bg-green-500"
                                        : "bg-red-500"
                                    }`}
                                  ></span>
                                  <span
                                    className={`font-medium ${
                                      transaction.type === "credit"
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {transaction.type === "credit"
                                      ? "Payment Received"
                                      : "Refund Issued"}
                                  </span>
                                </div>
                                <div className="mt-1 text-sm text-gray-500 flex items-center">
                                  <span>{transaction.paymentMethod}</span>
                                  <span className="mx-2">•</span>
                                  <span>
                                    {formatTransactionDate(
                                      transaction.createdAt
                                    )}
                                  </span>
                                </div>
                              </div>
                              <div
                                className={`font-medium text-lg ${
                                  transaction.type === "credit"
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {transaction.type === "credit" ? "+" : "-"}{" "}
                                {formatCurrency(transaction.amount)}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {/* Payment Summary */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="text-lg font-medium text-gray-800 mb-4">
                    Payment Summary
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-gray-600">
                      <span>Subtotal</span>
                      <span>{formatCurrency(booking.totalAmount)}</span>
                    </div>

                    {booking.discount > 0 && (
                      <div className="flex justify-between items-center text-green-600">
                        <span>Discount Applied</span>
                        <span>- {formatCurrency(booking.discount)}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center font-serif font-bold text-xl pt-3 border-t border-gray-200">
                      <span>Total Amount</span>
                      <span>
                        {formatCurrency(booking.totalAmount - booking.discount)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-gray-600 pt-3 border-t border-gray-200">
                      <span>Amount Paid</span>
                      <span>{formatCurrency(booking.amountPaid)}</span>
                    </div>

                    {booking.paymentStatus !== "paid" &&
                      booking.paymentStatus !== "refunded" && (
                        <div className="flex justify-between items-center text-red-600 font-medium">
                          <span>Balance Due</span>
                          <span>
                            {formatCurrency(
                              booking.totalAmount -
                                booking.discount -
                                booking.amountPaid
                            )}
                          </span>
                        </div>
                      )}
                  </div>
                </div>

                {/* Payment Button if needed */}
                {(booking.paymentStatus === "pending" ||
                  booking.paymentStatus === "partial") && (
                  <div className="mt-6 print:hidden">
                    <button className="w-full bg-gradient-to-r from-amber-700 to-amber-900 text-white py-4 rounded-lg font-medium text-lg flex items-center justify-center hover:from-amber-800 hover:to-amber-950 transition shadow-lg">
                      <FaCreditCard className="mr-2" />
                      Complete Payment
                    </button>
                    <p className="text-center text-gray-500 text-sm mt-2">
                      Secure payment processing
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hotel Policies */}
          <div
            className={`${
              activeSection !== "policies" && "hidden print:block"
            } mt-12`}
          >
            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center">
              <span className="w-8 h-8 rounded-full bg-amber-800 text-white flex items-center justify-center mr-3 text-sm">
                <FaLock />
              </span>
              Hotel Policies & Information
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md border border-amber-100">
                <h4 className="font-bold text-lg text-amber-800 mb-4 pb-2 border-b border-amber-100">
                  Check-in & Check-out Protocol
                </h4>
                <ul className="text-gray-600 space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Check-in begins at <strong>12:00 PM</strong>. Rooms may be
                      available earlier, subject to availability.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Check-out time is <strong>12:00 PM</strong>. Late
                      check-out can be arranged with our front desk for an
                      additional fee.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Our 24-hour concierge team is available to accommodate
                      special check-in/check-out requirements with advance
                      notice.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md border border-amber-100">
                <h4 className="font-bold text-lg text-amber-800 mb-4 pb-2 border-b border-amber-100">
                  Cancellation & Modification Policy
                </h4>
                <ul className="text-gray-600 space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Free cancellation up to <strong>48 hours</strong> before
                      scheduled check-in. No penalties apply.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Cancellations within 48 hours incur a charge of{" "}
                      <strong>50% of the total reservation</strong>.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Cancellations on the same day require payment of the <strong>full amount</strong> of the reservation.
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-white p-6 rounded-xl shadow-md border border-amber-100">
                <h4 className="font-bold text-lg text-amber-800 mb-4 pb-2 border-b border-amber-100">
                  Premium Services
                </h4>
                <ul className="text-gray-600 space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Complimentary butler service available upon request for
                      suite bookings.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Daily turndown service with 
                      mineral water.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      24-hour in-room dining with a curated menu by our
                      executive chef.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md border border-amber-100">
                <h4 className="font-bold text-lg text-amber-800 mb-4 pb-2 border-b border-amber-100">
                  Special Accommodations
                </h4>
                <ul className="text-gray-600 space-y-3">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Special dietary requirements can be accommodated with 24
                      hours notice.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Wellness services including in-room massage and meditation
                      sessions available.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span>
                      Personalized welcome amenities can be arranged through our
                      concierge.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};
export default BookingConfirmation;
